import AuditoriasGateway from '@/gateways/AuditoriasGateway'

export const STATUS = {
  LOADING: 'loading',
  OK: 'ok',
  NOT_CONFIGURED: 'not_configured',
  UNAVAILABLE: 'unavailable'
}

const REASON_NOT_CONFIGURED = 'not_configured'

function toDate (value) {
  if (!value) return null
  const dateOnly = value.indexOf('T') === -1 ? value : value.slice(0, 10)
  const parsed = new Date(dateOnly + 'T00:00:00')
  return isNaN(parsed.getTime()) ? null : parsed
}

function mostRecentFirst (a, b) {
  const dateA = toDate(a.fecha)
  const dateB = toDate(b.fecha)
  if (dateA === null && dateB === null) return 0
  if (dateA === null) return 1
  if (dateB === null) return -1
  return dateB - dateA
}

function hasResult (audit) {
  if (!audit) return false
  const scale = audit.escala
  return scale !== null && scale !== undefined && String(scale).trim() !== ''
}

const state = {
  audits: null,
  activeInterventions: [],
  status: STATUS.LOADING
}

const getters = {
  getAudits: state => state.audits,
  getStatus: state => state.status,
  getActiveInterventions: state => state.activeInterventions,

  isLoading: state => state.status === STATUS.LOADING,
  isNotConfigured: state => state.status === STATUS.NOT_CONFIGURED,
  isUnavailable: state => state.status === STATUS.UNAVAILABLE,

  isEmpty: state =>
    state.status === STATUS.OK && state.audits !== null && state.audits.length === 0,

  getRatedAudits: state => {
    if (state.audits === null) return []
    return state.audits.filter(hasResult).sort(mostRecentFirst)
  },

  getScheduledAudits: state => {
    if (state.audits === null) return []
    return state.audits.filter(audit => !hasResult(audit)).sort(mostRecentFirst)
  },

  getLatestAudit: (state, getters) => {
    const rated = getters.getRatedAudits
    return rated.length === 0 ? null : rated[0]
  },

  getLatestAudits: (state, getters) => limit => getters.getRatedAudits.slice(0, limit),

  getScaleSummary: (state, getters) => {
    const rated = getters.getRatedAudits
    const counts = {}

    rated.forEach(audit => {
      const scale = audit.escala
      counts[scale] = (counts[scale] || 0) + 1
    })

    return {
      total: rated.length,
      byScale: counts
    }
  }
}

const mutations = {
  SET_AUDITS (state, audits) {
    state.audits = audits
    state.status = STATUS.OK
  },
  SET_ACTIVE_INTERVENTIONS (state, interventions) {
    state.activeInterventions = interventions
  },
  SET_STATUS (state, status) {
    state.status = status
    state.audits = []
  },
  RESET_FOR_RETRY (state) {
    state.status = STATUS.LOADING
    state.audits = null
  }
}

const actions = {
  async GET_AUDITS ({ commit }) {
    try {
      const { data } = await AuditoriasGateway.listar()

      if (!data.success) {
        commit('SET_STATUS', data.reason === REASON_NOT_CONFIGURED
          ? STATUS.NOT_CONFIGURED
          : STATUS.UNAVAILABLE)
        return
      }

      commit('SET_AUDITS', data.data || [])
    } catch (error) {
      commit('SET_STATUS', STATUS.UNAVAILABLE)
    }
  },

  async GET_INTERVENTIONS ({ commit }) {
    try {
      const { data } = await AuditoriasGateway.intervenciones()
      const active = (data.data && data.data.activas) || []
      commit('SET_ACTIVE_INTERVENTIONS', active)
    } catch (error) {
      commit('SET_ACTIVE_INTERVENTIONS', [])
    }
  },

  async RETRY_LOAD ({ commit, dispatch }) {
    commit('RESET_FOR_RETRY')
    await Promise.all([
      dispatch('GET_AUDITS'),
      dispatch('GET_INTERVENTIONS')
    ])
  }
}

export default { namespaced: true, state, getters, mutations, actions }
